import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
  computed,
  inject,
  signal
} from '@angular/core';
import {NavbarItem} from "./models/navbar-item";
import {NavbarService} from "../navbar-base/navbar.service";
import {ItemBase} from "../navbar-base/item-base";
import {Router, RouterLink} from "@angular/router";
import {WindowStateService} from "../../_services/window-state.service";
import {WindowState} from "../../x-models/window-state";
import {PlatformScannerService} from "../../_services/platform-scanner.service";
import {Platform} from "../../enums/platform";
import {AuthenticationService} from "../../_auth/authentication.service";
import {Roles} from "../../_auth/x-enums/roles";
import {AppRoutes} from "../../enums/routes";
import {ContractType} from "../navbar-base/_models/contract-type";
import {CommonModule} from "@angular/common";
import {NavbarOfficeItemComponent} from "./navbar-office-item/navbar-office-item.component";
import {NgSelectComponent} from "@ng-select/ng-select";
import {FormsModule} from "@angular/forms";
import {Subscription} from "rxjs";
import {ContextSwitcherComponent} from "../../_auth/context/context-switcher.component";
import {ViewportService} from "../../_services/viewport.service";

@Component({
  selector: 'app-navbar-office',
  templateUrl: './navbar-office.component.html',
  styleUrls: ['./navbar-office.component.scss'],
  imports: [
    CommonModule,
    NavbarOfficeItemComponent,
    NgSelectComponent,
    RouterLink,
    FormsModule,
    ContextSwitcherComponent
  ]
})
export class NavbarOfficeComponent implements AfterViewInit, OnDestroy {
  @ViewChild("searchElement") searchElement: ElementRef | undefined;
  @ViewChild("navbarContainer") navbarContainer: ElementRef | undefined;
  @ViewChild("tabsContainer") tabsContainer: ElementRef | undefined;
  @ViewChild("searchContainer") searchContainer: ElementRef | undefined;

  last: NavbarItem | undefined;
  selected: ItemBase | null | undefined;
  @ViewChild("dropdown") dropdown!: ElementRef;
  focus: boolean = false;
  menuVisible = false;
  moreVisible = false;

  visibleItems: NavbarItem[] = [];
  overflowItems: NavbarItem[] = [];
  protected readonly console = console;
  protected readonly Platform = Platform;
  protected readonly Routes = AppRoutes;
  protected readonly ContractType = ContractType;
  private resizeObserver: ResizeObserver | undefined;
  private tabLoadedSubscription: Subscription | undefined;

  private readonly viewport = inject(ViewportService);

  /**
   * Whether the phone-width search has been expanded into its full field (#602).
   *
   * Only meaningful below Tailwind's `sm`; from `sm` upwards the field is always on screen and this flag is
   * ignored, so nothing has to reset it when the window is widened.
   */
  private readonly searchExpanded = signal(false);

  /** The search is a single icon button right now — phone width, field not yet asked for. */
  protected readonly searchCollapsed = computed(() => this.viewport.isBelowSm() && !this.searchExpanded());

  /**
   * The phone search is open, so the bar belongs to the field: the logo and the context chip step aside for it.
   * At `sm` and above this is always false — there the field and the rest of the bar fit side by side.
   */
  protected readonly searchExpandedOnPhone = computed(() =>
    this.viewport.isBelowSm() && this.searchExpanded());

  constructor(public NavbarService: NavbarService, private router: Router,
              private windowStateService: WindowStateService, public PlatformService: PlatformScannerService,
              public AuthenticationService: AuthenticationService, private cdr: ChangeDetectorRef) {
    // this.select(this.NavbarService.navbarItems[0])
    this.handleWindowState(this.windowStateService.state);
    this.tabLoadedSubscription = this.windowStateService.tabLoaded.subscribe({
      next: (x: WindowState | undefined) => {
        this.handleWindowState(x);
      }
    })
  }

  /** Human label for the signed-in principal's primary role (shown under their name in the mobile menu). */
  protected get roleLabel(): string {
    const perms = this.AuthenticationService.userValue?.permissions ?? [];
    if (perms.includes(Roles.PlatformOwner)) return 'Plattform-Eigentümer';
    if (perms.includes('CompanyOwner')) return 'Inhaber';
    if (perms.includes(Roles.ShardAdmin)) return 'Administrator';
    if (perms.includes(Roles.Payroll)) return 'Lohnbuchhalter';
    // Below Payroll on purpose: somebody who does both jobs for a firm is introduced by the one they were
    // engaged for first, and this list answers with the FIRST match rather than with everything held.
    if (perms.includes(Roles.Steuerberater)) return 'Steuerberater';
    if (perms.includes(Roles.Employee)) return 'Mitarbeiter';
    return '';
  }

  ngAfterViewInit(): void {
    this.visibleItems = this.NavbarService.getAvailable();
    this.overflowItems = [];

    this.resizeObserver = new ResizeObserver(() => {
      this.checkOverflow();
    });

    if (this.navbarContainer) {
      this.resizeObserver.observe(this.navbarContainer.nativeElement);
    }

    // Initial check after a short delay to ensure elements are rendered
    setTimeout(() => {
      this.checkOverflow();
      // Second check after another delay just in case items are still settling
      setTimeout(() => this.checkOverflow(), 500);
    }, 100);
  }

  @HostListener('document:click', ['$event'])
  clickout(event: any) {
    if (this.moreVisible && this.dropdown && !this.dropdown.nativeElement.contains(event.target)) {
      this.moreVisible = false;
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.tabLoadedSubscription) {
      this.tabLoadedSubscription.unsubscribe();
    }
  }

  checkOverflow() {
    if (!this.navbarContainer || !this.searchContainer || !this.tabsContainer) return;

    const navbarWidth = this.navbarContainer.nativeElement.offsetWidth;
    const minSearchWidth = navbarWidth * 0.20;

    // Reset items to calculate widths
    const allAvailable = this.NavbarService.getAvailable();

    // If we're already overflowing, we need to check if we can move items back
    // or if we need to move more items out.

    // To do this properly without layout thrashing, we might need a more robust approach.
    // For now, let's try a simple iterative approach.

    let currentVisible = [...allAvailable];
    let currentOverflow: NavbarItem[] = [];

    // Temporary set all visible to measure
    this.visibleItems = [...allAvailable];
    this.overflowItems = [];
    this.cdr.detectChanges();

    // Measure tabs container width when all items are there
    // Since we are using flex, this is tricky.
    // Let's measure individual items if possible or just the container.

    const tabs = this.tabsContainer.nativeElement.children;
    const tabWidths: number[] = [];
    for (let i = 0; i < tabs.length; i++) {
      tabWidths.push(tabs[i].offsetWidth);
    }

    // Now calculate how many items fit while keeping 20% for search
    const availableWidthForTabs = navbarWidth - minSearchWidth - 250; // 250 for logo and right actions approx
    // We should probably measure the logo and right side too for better accuracy.

    let totalTabsWidth = 0;
    let fitIndex = allAvailable.length;

    // Add a small buffer for the "More" button (approx 80px)
    const moreButtonWidth = 80;

    for (let i = 0; i < tabWidths.length; i++) {
      const nextWidth = totalTabsWidth + tabWidths[i];
      if (nextWidth > availableWidthForTabs - (i < tabWidths.length - 1 ? moreButtonWidth : 0)) {
        fitIndex = i;
        break;
      }
      totalTabsWidth = nextWidth;
    }

    if (fitIndex < allAvailable.length) {
      this.visibleItems = allAvailable.slice(0, fitIndex);
      this.overflowItems = allAvailable.slice(fitIndex);
    } else {
      this.visibleItems = allAvailable;
      this.overflowItems = [];
    }

    this.cdr.detectChanges();
  }

  @HostListener('window:resize')
  onResize() {
    this.checkOverflow();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'k' && !event.shiftKey) {
      event.preventDefault();
      // The shortcut must still open the field on a phone, where the bar shows only the search ICON (#602).
      // Expanding first and focusing afterwards is the whole of it: focusing a `display: none` element does
      // nothing at all and reports no error, so the shortcut would silently stop working at that width.
      this.expandSearch();
    }
  }

  /**
   * Swaps the phone-width search icon for the real field and puts the caret in it.
   *
   * Idempotent: at `sm` and above the field is already on screen, so this is just a focus call, which is what
   * makes it safe to route the ⌘K shortcut through it at every width.
   */
  protected expandSearch(): void {
    this.searchExpanded.set(true);
    this.cdr.detectChanges();
    // After the field exists, not before. `detectChanges` above renders it; the focus call then has an element
    // to land on rather than a comment node.
    // @ts-ignore — `#searchElement` resolves to the NgSelectComponent instance, which owns `focus()`.
    this.searchElement?.focus();
  }

  /** Puts the phone bar back: the field folds away and the logo and context chip return. */
  protected collapseSearch(): void {
    this.searchExpanded.set(false);
  }

  handleWindowState(state: WindowState | undefined) {
    if (!state) return;
    let item = this.NavbarService.getAvailable().find(i => i.title == state.tab);
    if (item) {
      this.last = item;
      if (state.expanded) {
        this.select(item)
      }
    }
  }

  select(item: NavbarItem) {
    this.last = item;
    this.NavbarService.navbarItems.forEach(i => {
      if (i.isSelected && i.title == item.title) {
        item.isExpanded = !item.isExpanded;
        item.isSelected = !item.isSelected;
        this.windowStateService.setTab(item.title, item.isExpanded);
      } else {
        i.isSelected = i.title == item.title;
        if (i.isSelected) {
          i.isExpanded = true;
          this.windowStateService.setTab(item.title, item.isExpanded);
        } else {
          i.isExpanded = false;
        }
        this.menuClose();
      }
    });
  }

  close() {
    this.NavbarService.navbarItems.forEach(i => {
      i.isSelected = false;
      i.isExpanded = false;
    });
  }

  loadItems() {
    this.focus = true;
    this.NavbarService.itemBases = [...this.NavbarService.itemBases.sort((a, b) => a.id.localeCompare(b.id))];
  }

  groupBy(item: ItemBase) {
    return item.breadcrumbs[0];
  }

  delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async navigate(item: ItemBase) {
    if (item.onSelectedFunction) {
      item.execute();
    } else if (typeof item.link == "object" && item.link != null) {
      this.router.navigate(item.link as any[], {queryParams: item.params}).then(_ => {
      });
    } else {
      this.router.navigate([item.link], {queryParams: item.params}).then(_ => {
      });
    }

    await this.delay(0);
    this.selected = undefined;
  }

  focusout() {
    this.focus = false;
  }

  search(term: string, item: ItemBase) {
    return item.contains(term);
  }

  menuToggle() {
    if (!this.menuVisible) this.menuOpen();
    else this.menuClose();
  }

  menuClose() {

    this.menuVisible = false;
  }

  menuOpen() {

    this.menuVisible = true;
  }

  /**
   * The mobile counterpart of the desktop "Benutzer wechseln" arrow (Bug #405).
   *
   * The order matters and is the whole reason this wrapper exists instead of calling
   * {@link AuthenticationService.switch} straight from the template: `switch()` drops the session and
   * *navigates* to `/login`. The hamburger overlay is `position: fixed` and lives outside the router outlet, so
   * it survives that navigation — leaving a full-screen menu sitting on top of the login form. Closing first
   * removes it from the DOM before the route changes.
   */
  protected switchUser(): void {
    this.menuClose();
    this.AuthenticationService.switch();
  }

  identify(index: any, item: ItemBase) {
    return item.id;
  }
}
